import React from 'react';
import { styled } from '@mui/material';
import {
  CalendarDay,
  numberToCurrencyString,
  numberToPercentageString,
} from 'further-ui/utils';
import { AllocationRule } from 'further-types/lp/allocations';
import { sortAllocations } from '../utils/sortAllocations';
import TabsPanel from 'further-ui/components/TabsPanel';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { Column } from 'further-ui/components/Layout';
import AvatarWithName from 'components/new/AvatarWithName';

const ContentColumn = styled(Column)(({ theme }) => ({
  padding: theme.spacing(
    0,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
  ),
}));

interface GeneralCapitalCallCardProps {
  generalCapitalAction: {
    actionId: string;
    entryId: string;
    name: string;
    ledgerDate: CalendarDay;
    actionDueDate?: CalendarDay;
    totalAmountLcy: number;
    allocationRule: AllocationRule;
    originalInvestorBreakdown: Array<{
      commitmentId: string;
      investorName: string;
      totalCommitmentLcy: number;
      calledFundsLcy: number;
      allocationPercentage: number;
    }>;
    newInvestorBreakdown: Array<{
      commitmentId: string;
      investorName: string;
      totalCommitmentLcy: number;
      calledFundsLcy: number;
      allocationPercentage: number;
      excludeFromAllocation: boolean;
      callOverpaidOrUnderpaid: number;
      isNewTransferee?: boolean;
    }>;
    numberOfEntries: number;
  };
  baseCurrency: string;
}

const GeneralCapitalCallCard: React.FC<GeneralCapitalCallCardProps> = ({
  generalCapitalAction,
  baseCurrency,
}) => {
  const sortedOriginalBreakdown = sortAllocations(
    generalCapitalAction.originalInvestorBreakdown,
  );
  const sortedNewBreakdown = sortAllocations(
    generalCapitalAction.newInvestorBreakdown,
  );

  const originalRows = sortedOriginalBreakdown.map((row, idx) => ({
    id: `original-${idx}`,
    ...row,
  }));

  const newRows = sortedNewBreakdown.map((row, idx) => ({
    id: `new-${idx}`,
    ...row,
  }));

  type OriginalRow = (typeof originalRows)[number];
  type NewRow = (typeof newRows)[number];

  const sharedColumns = <T extends OriginalRow>(): Array<TableColumn<T>> => [
    {
      key: 'investorName',
      label: 'Investor',
      render: (row) => <AvatarWithName name={row.investorName} />,
    },
    {
      key: 'totalCommitmentLcy',
      label: 'Total commitment',
      render: (row) =>
        numberToCurrencyString(row.totalCommitmentLcy, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'calledFundsLcy',
      label: 'Called funds (LCY)',
      render: (row) =>
        numberToCurrencyString(row.calledFundsLcy, {
          fallback: '-',
          currency: baseCurrency,
        }),
    },
    {
      key: 'allocationPercentage',
      label: 'Allocation % of called funds',
      render: (row) =>
        numberToPercentageString(row.allocationPercentage, {
          accuracy: 2,
          noMultiply: true,
        }),
    },
  ];

  const newColumns: Array<TableColumn<NewRow>> = [
    ...sharedColumns<NewRow>(),
    {
      key: 'callOverpaidOrUnderpaid',
      label: 'Call underpaid/(overpaid)',
      render: (row) =>
        numberToCurrencyString(row.callOverpaidOrUnderpaid, {
          fallback: '-',
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
  ];

  return (
    <ContentColumn>
      <TabsPanel
        defaultTab="after"
        tabs={[
          {
            key: 'after',
            label: 'New allocation',
            content: (
              <Table
                showTopBorder
                showHeaderBorder
                columns={newColumns}
                rows={newRows}
                scrollable={true}
              />
            ),
          },
          {
            key: 'before',
            label: 'Original allocation',
            content: (
              <Table
                showTopBorder
                showHeaderBorder
                columns={sharedColumns()}
                rows={originalRows}
                scrollable={true}
              />
            ),
          },
        ]}
      />
    </ContentColumn>
  );
};

export default GeneralCapitalCallCard;
